import { Table } from '@startuptools/ui/Table';
import { DisplayIdentity, Identity } from '@startuptools/business-ui/identity/Identity';
import { EditableEmail } from '../../../components/editable-email/EditableEmail';
import { RefetchQueriesInclude } from '@apollo/client';
import { IconButton } from '@startuptools/ui/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { GqlCreateIdentityInput, useSetIdentityEmailMutation } from '../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useState } from 'react';
import { AlertConfirmDialog } from '../../../components/simple-dialogs/AlertConfirmDialog';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { Trans } from '@lingui/react';
import { InputState, JuridicalKind } from '@startuptools/business-ui/identity/types';
import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';

export const BoardMembersTable = ({
  boardMembers,
  onDelete,
  onCreate,
  refetchQueries,
}: {
  boardMembers: { id: string; identity: DisplayIdentity }[];
  onDelete: (id: string) => Promise<unknown>;
  onCreate: (identity: GqlCreateIdentityInput) => Promise<unknown>;
  refetchQueries: RefetchQueriesInclude;
}) => {
  const companyId = useCompanyId();
  // eslint-disable-next-line sut/loading-mutation
  const [doUpdateEmail] = useSetIdentityEmailMutation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showIdentityDialog, setShowIdentityDialog] = useState(false);

  const handleSetEmail = async (id: string, email: string | null) => {
    return doUpdateEmail({ variables: { companyId, input: { id, email } } });
  };

  return (
    <>
      <Stack gap={3}>
        <Box>
          <Button variant="outlined" onClick={() => setShowIdentityDialog(true)}>
            <Trans id="Add a board member that should sign the statement" />
          </Button>
        </Box>
        <Table>
          <tbody>
            {boardMembers.map(bm => (
              <tr key={bm.id}>
                <td>
                  <Identity identity={bm.identity} />
                </td>
                <td>
                  <EditableEmail
                    onChange={email => handleSetEmail(bm.identity.id, email)}
                    refetchQueries={refetchQueries}
                  >
                    {bm.identity.email}
                  </EditableEmail>
                </td>
                <td align="right">
                  <IconButton variant="outlined" color="danger" onClick={() => setShowDeleteConfirm(bm.id)}>
                    <DeleteIcon />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Stack>
      {showDeleteConfirm && (
        <AlertConfirmDialog
          onClose={() => setShowDeleteConfirm(null)}
          onConfirm={() => onDelete(showDeleteConfirm).then(() => setShowDeleteConfirm(null))}
        />
      )}
      {showIdentityDialog && (
        <AddIdentity
          headline={<Trans id="Add board member" />}
          juridicalKinds={[JuridicalKind.Person]}
          availableInputStates={{
            [InputState.System]: true,
            [InputState.CompanyBoard]: false,
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
          }}
          relations={{ companyDirector: true }}
          defaultInputState={InputState.System}
          detailFieldsSettings={{}}
          handleCreate={identity => onCreate(identityToInput(identity)).then(() => setShowIdentityDialog(false))}
          onClose={() => setShowIdentityDialog(false)}
        />
      )}
    </>
  );
};
